import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  SkeletonLoader,
  Tag,
  Text,
} from '@equip.health/ui';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';
import { TreatmentOverviewCardDiagnosisSection } from './TreatmentOverviewCardDiagnosisSection';
import { TreatmentOverviewCardDocumentsSection } from './TreatmentOverviewCardDocumentsSection';
import { Dispatch, useState } from 'react';
import { AssessmentAction } from '~/lib/constants/patients.constants';
import { TreatmentLevelIdToTagColorMap } from './TreatmentLevelIdToTagColorMap';
import { TreatmentOverviewCardAttributeRow } from './TreatmentOverviewCardAttributeRow';
import { EditTreatmentOverviewModal } from './EditTreatmentOverviewModal';
import { useTreatmentAttributes } from '~/lib/hooks/useTreatmentAttributes';
import { TypographyVariant } from '@equip.health/ui/lib/types/src/components/Text/Text';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { getOpenTreatmentAttribute } from '~/lib/util/treatmentEpisodes.util';

type Props = {
  patientId: string;
  noteId?: string;
  updateAssessmentState?: Dispatch<AssessmentAction>;
  style?: {
    titleVariant?: TypographyVariant;
  };
};

export const TreatmentOverviewCard = ({
  patientId,
  noteId,
  updateAssessmentState,
  style,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const { isLoading: isGetPatientDiagnosesLoading } =
    useGetPatientDiagnosesQuery(patientId);

  const {
    activeTreatmentEpisode,
    discussionOfWeights,
    modalities,
    forms,
    phaseStages,
    levels,
    refeedingRisks,
    saferStages,
    medicalStabilities,
    psychiatricStabilities,
    isLoading: isGetPatientAttributesLoading,
  } = useTreatmentAttributes(patientId);

  const [isEditOverviewModalOpen, setIsOverviewModalOpen] = useState(false);

  const openLevel = getOpenTreatmentAttribute(levels);
  const openModality = getOpenTreatmentAttribute(modalities);
  const openForm = getOpenTreatmentAttribute(forms);
  const openPhaseStage = getOpenTreatmentAttribute(phaseStages);
  const openDiscussionOfWeight = getOpenTreatmentAttribute(discussionOfWeights);
  const openSaferStage = getOpenTreatmentAttribute(saferStages);
  const openRefeedingRisk = getOpenTreatmentAttribute(refeedingRisks);
  const openMedicalStability = getOpenTreatmentAttribute(medicalStabilities);
  const openPsychiatricStability = getOpenTreatmentAttribute(
    psychiatricStabilities,
  );

  if (!activeTreatmentEpisode) {
    return <></>;
  }

  const isLoading =
    isGetPatientDiagnosesLoading || isGetPatientAttributesLoading;

  if (isLoading) {
    return (
      <ChakraBox margin="18px">
        <SkeletonLoader
          height="514px"
          id="treatment-overview-loading"
          width="350px"
        />
      </ChakraBox>
    );
  }

  const noTreatmentAttributes =
    !discussionOfWeights?.length &&
    !levels?.length &&
    !modalities?.length &&
    !forms?.length &&
    !phaseStages?.length &&
    !refeedingRisks?.length &&
    !saferStages?.length &&
    !medicalStabilities?.length &&
    !psychiatricStabilities?.length;

  return (
    <ChakraBox
      data-testid="treatment-overview-card"
      margin={noteId ? '18px 0' : '18px'}
    >
      <ChakraFlex justifyContent="space-between" paddingX="4px">
        <Text variant={style?.titleVariant ?? 'h5'}>Treatment overview</Text>
        {!noTreatmentAttributes && (
          <FireflyButton
            id="treatment-overview-edit-btn"
            leftIcon={FAIconName.penRegular}
            onClick={() => {
              trackEvent(CarePlanEvents.clickOnEditOverviewButton);
              setIsOverviewModalOpen(true);
            }}
            size="small"
            variant="tertiary"
          >
            Edit overview
          </FireflyButton>
        )}
      </ChakraFlex>
      <ChakraBox
        borderColor="neutral.line"
        borderRadius="12px"
        borderWidth="1px"
        marginTop="18px"
        padding="18px"
      >
        <TreatmentOverviewCardDiagnosisSection patientId={patientId} />
        {openLevel && (
          <TreatmentOverviewCardAttributeRow
            label="Level"
            value={
              <Tag
                color={TreatmentLevelIdToTagColorMap[openLevel.level.id]}
                id={`treatment-overview-level-tag-${activeTreatmentEpisode.treatmentEpisodeId}`}
                value={openLevel.level.label}
              />
            }
          />
        )}
        {openModality && (
          <TreatmentOverviewCardAttributeRow
            label="Modality"
            value={<Text>{openModality.modality.label}</Text>}
          />
        )}
        {openForm && (
          <TreatmentOverviewCardAttributeRow
            label="Form"
            value={<Text>{openForm.form.label}</Text>}
          />
        )}
        {openPhaseStage && (
          <TreatmentOverviewCardAttributeRow
            label="Phase stage"
            value={<Text>{openPhaseStage.phaseStage.label}</Text>}
          />
        )}
        {openDiscussionOfWeight && (
          <TreatmentOverviewCardAttributeRow
            label="Weight discussion"
            value={
              <Text>{openDiscussionOfWeight.discussionOfWeight.label}</Text>
            }
          />
        )}
        {openSaferStage && (
          <TreatmentOverviewCardAttributeRow
            label="SAFER stage"
            value={<Text>{openSaferStage.saferStage.label}</Text>}
          />
        )}
        {openRefeedingRisk && (
          <TreatmentOverviewCardAttributeRow
            label="Refeeding risk"
            value={<Text>{openRefeedingRisk.refeedingRisk.label}</Text>}
          />
        )}
        {openMedicalStability && (
          <TreatmentOverviewCardAttributeRow
            label="Medical stability"
            value={<Text>{openMedicalStability.medicalStability.label}</Text>}
          />
        )}
        {openPsychiatricStability && (
          <TreatmentOverviewCardAttributeRow
            label="Psychiatric stability"
            value={
              <Text>{openPsychiatricStability.psychiatricStability.label}</Text>
            }
          />
        )}

        {!isLoading && noTreatmentAttributes && (
          <ChakraFlex
            alignItems="center"
            borderColor="neutral.line"
            borderRadius="12px"
            borderStyle="dashed"
            borderWidth="1px"
            flexDirection="column"
            gap="16px"
            height="102px"
            marginTop="18px"
            padding="18px"
            width="314px"
          >
            <Text color="neutral.secondary" variant="caption">
              No treatment attributes have been added.
            </Text>
            <FireflyButton
              id="treatment-overview-empty-add-attributes"
              leftIcon={FAIconName.plusSolid}
              onClick={() => {
                trackEvent(CarePlanEvents.clickOnAddAttributesButton);
                setIsOverviewModalOpen(true);
              }}
              size="small"
              variant="secondary"
            >
              Add attributes
            </FireflyButton>
          </ChakraFlex>
        )}
        {updateAssessmentState && (
          <TreatmentOverviewCardDocumentsSection
            patientId={patientId}
            updateAssessmentState={updateAssessmentState}
          />
        )}
      </ChakraBox>
      <EditTreatmentOverviewModal
        isOpen={isEditOverviewModalOpen}
        noteId={noteId}
        onClose={() => {
          setIsOverviewModalOpen(false);
        }}
        patientId={patientId}
      />
    </ChakraBox>
  );
};
